import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ProfileWithImpactSummary = {
  userId: string;
  fullName: string;
  avatarUrl: string | null;
  role: string;
  responseCount: number;
  surveyCount: number;
  latestWaveLabel: string;
};

export type ImpactSurveyAnswer = {
  questionId: string;
  questionText: string;
  questionType: string;
  impactMetricKey: string | null;
  answerText: string | null;
  answerNumber: number | null;
  answerChoice: { selected: string } | null;
  displayOrder: number;
};

export type ImpactSurveyWave = {
  waveLabel: string;
  completedAt: string;
  answers: ImpactSurveyAnswer[];
};

export type ImpactSurveyData = {
  surveyId: string;
  surveyTitle: string;
  waves: ImpactSurveyWave[];
};

/**
 * Lists all users with at least one completed impact survey response.
 */
export function useImpactSurveyUsers() {
  const query = useQuery({
    queryKey: ["impact_survey_users"],
    queryFn: async (): Promise<ProfileWithImpactSummary[]> => {
      // Pull completed responses joined to impact surveys only
      const { data: responses, error } = await supabase
        .from("platform_survey_responses")
        .select(
          `id, user_id, survey_id, wave_label, completed_at,
           platform_surveys!inner(id, survey_type)`
        )
        .not("completed_at", "is", null)
        .eq("platform_surveys.survey_type", "impact");
      if (error) throw error;

      const rows = (responses || []) as any[];
      if (rows.length === 0) return [];

      const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
      const { data: profiles, error: pErr } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role")
        .in("id", userIds);
      if (pErr) throw pErr;
      const profileById = new Map(
        (profiles || []).map((p: any) => [p.id, p])
      );

      const byUser = new Map<
        string,
        { responseCount: number; surveyIds: Set<string>; latest: { wave: string; at: string } }
      >();

      for (const r of rows) {
        const cur = byUser.get(r.user_id) || {
          responseCount: 0,
          surveyIds: new Set<string>(),
          latest: { wave: r.wave_label, at: r.completed_at },
        };
        cur.responseCount += 1;
        cur.surveyIds.add(r.survey_id);
        if (
          !cur.latest.at ||
          new Date(r.completed_at).getTime() > new Date(cur.latest.at).getTime()
        ) {
          cur.latest = { wave: r.wave_label, at: r.completed_at };
        }
        byUser.set(r.user_id, cur);
      }

      const summaries: ProfileWithImpactSummary[] = Array.from(
        byUser.entries()
      ).map(([userId, agg]) => {
        const p: any = profileById.get(userId) || {};
        return {
          userId,
          fullName: p.full_name || "Unknown User",
          avatarUrl: p.avatar_url || null,
          role: p.role || "",
          responseCount: agg.responseCount,
          surveyCount: agg.surveyIds.size,
          latestWaveLabel: agg.latest.wave,
        };
      });

      summaries.sort((a, b) =>
        a.fullName.localeCompare(b.fullName, undefined, { sensitivity: "base" })
      );
      return summaries;
    },
  });

  return {
    users: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Lazy: fetches detailed impact survey responses for a single user.
 * Should only be called when a user row is expanded (controlled via `enabled`).
 */
export function useUserImpactDetail(userId: string | null) {
  const query = useQuery({
    queryKey: ["user_impact_detail", userId],
    enabled: !!userId,
    queryFn: async (): Promise<ImpactSurveyData[]> => {
      const { data: responses, error } = await supabase
        .from("platform_survey_responses")
        .select(
          `id, survey_id, wave_label, completed_at,
           platform_surveys!inner(id, title, survey_type),
           platform_survey_answers(
             question_id, answer_text, answer_number, answer_choice
           )`
        )
        .eq("user_id", userId!)
        .not("completed_at", "is", null)
        .eq("platform_surveys.survey_type", "impact");
      if (error) throw error;

      const rows = (responses || []) as any[];
      if (rows.length === 0) return [];

      // Collect question ids to fetch question metadata
      const questionIds = new Set<string>();
      rows.forEach((r) => {
        (r.platform_survey_answers || []).forEach((a: any) =>
          questionIds.add(a.question_id)
        );
      });

      let questionsById = new Map<string, any>();
      if (questionIds.size > 0) {
        const { data: questions, error: qErr } = await supabase
          .from("platform_survey_questions")
          .select(
            "id, question_text, question_type, impact_metric_key, display_order"
          )
          .in("id", Array.from(questionIds));
        if (qErr) throw qErr;
        questionsById = new Map((questions || []).map((q: any) => [q.id, q]));
      }

      // Group by survey -> waves
      const bySurvey = new Map<
        string,
        { surveyTitle: string; waves: ImpactSurveyWave[] }
      >();

      for (const r of rows) {
        const surveyId = r.survey_id as string;
        const surveyTitle = r.platform_surveys?.title || "Untitled Survey";
        const bucket =
          bySurvey.get(surveyId) || { surveyTitle, waves: [] as ImpactSurveyWave[] };

        const answers: ImpactSurveyAnswer[] = (
          r.platform_survey_answers || []
        ).map((a: any) => {
          const q = questionsById.get(a.question_id);
          return {
            questionId: a.question_id,
            questionText: q?.question_text || "",
            questionType: q?.question_type || "text",
            impactMetricKey: q?.impact_metric_key || null,
            answerText: a.answer_text ?? null,
            answerNumber:
              a.answer_number !== null && a.answer_number !== undefined
                ? Number(a.answer_number)
                : null,
            answerChoice: a.answer_choice ?? null,
            displayOrder: q?.display_order ?? 0,
          };
        });

        answers.sort((a, b) => a.displayOrder - b.displayOrder);

        bucket.waves.push({
          waveLabel: r.wave_label,
          completedAt: r.completed_at,
          answers,
        });
        bySurvey.set(surveyId, bucket);
      }

      // Sort waves newest-first within each survey
      const out: ImpactSurveyData[] = Array.from(bySurvey.entries()).map(
        ([surveyId, v]) => ({
          surveyId,
          surveyTitle: v.surveyTitle,
          waves: v.waves.sort(
            (a, b) =>
              new Date(b.completedAt).getTime() -
              new Date(a.completedAt).getTime()
          ),
        })
      );

      // Sort surveys alphabetically for stable rendering
      out.sort((a, b) => a.surveyTitle.localeCompare(b.surveyTitle));
      return out;
    },
  });

  return {
    impactData: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
  };
}
